import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
export type SortKey = 'recommended' | 'price-asc' | 'price-desc' | 'name-asc' | 'name-desc';
export const SORT_OPTIONS: {
  value: SortKey;
  label: string;
}[] = [{
  value: 'recommended',
  label: 'Recommended'
}, {
  value: 'price-asc',
  label: 'Price: Low to High'
}, {
  value: 'price-desc',
  label: 'Price: High to Low'
}, {
  value: 'name-asc',
  label: 'Name: A to Z'
}, {
  value: 'name-desc',
  label: 'Name: Z to A'
}];
export type PageSize = 8 | 16 | 32 | 100 | 'all';
export const PAGE_SIZE_OPTIONS: {
  value: PageSize;
  label: string;
}[] = [{
  value: 8,
  label: '8'
}, {
  value: 16,
  label: '16'
}, {
  value: 32,
  label: '32'
}, {
  value: 100,
  label: '100'
}, {
  value: 'all',
  label: 'All'
}];
const selectClasses = 'h-9 rounded-md border border-brand-border bg-white text-sm text-brand-charcoal px-3 pr-8 font-medium focus:outline-none focus:ring-2 focus:ring-brand-maroon/30 focus:border-brand-maroon transition-colors appearance-none bg-no-repeat';
const selectStyle: React.CSSProperties = {
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b6b6b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
  backgroundPosition: 'right 0.5rem center'
};
export function ListingToolbar({
  sort,
  onSortChange,
  pageSize,
  onPageSizeChange,
  resultLabel
}: {
  sort: SortKey;
  onSortChange: (s: SortKey) => void;
  pageSize: PageSize;
  onPageSizeChange: (p: PageSize) => void;
  resultLabel: string;
}) {
  return <div className="border border-brand-border bg-white rounded-md px-4 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-id="element-123" data-mp="1uw2kknjp03fr">
      <p className="text-sm text-brand-muted order-2 sm:order-1" data-id="element-124" data-mp="1q3hr5n4p7scr">
        {resultLabel}
      </p>

      <div className="flex flex-col sm:flex-row gap-3 order-1 sm:order-2" data-id="element-125" data-mp="3kdg77w5irrnb">
        <label className="flex items-center gap-2 text-sm" data-id="element-126" data-mp="3papw9aozy1am">
          <span className="text-brand-muted shrink-0" data-id="element-127" data-mp="3an4r7b8k9s02">Sort</span>
          <select value={sort} onChange={e => onSortChange(e.target.value as SortKey)} className={selectClasses + ' w-full sm:w-auto'} style={selectStyle} aria-label="Sort products" data-id="element-128" data-mp="3amlafxund9nm">
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value} data-id="element-129" data-mp="35p3a2vup42r8">
                {o.label}
              </option>)}
          </select>
        </label>

        <label className="flex items-center gap-2 text-sm" data-id="element-130" data-mp="2wmk8ef2nbw6k">
          <span className="text-brand-muted shrink-0" data-id="element-131" data-mp="2wp6hij56qjqw">Per page</span>
          <select value={String(pageSize)} onChange={e => {
          const v = e.target.value;
          onPageSizeChange(v === 'all' ? 'all' : Number(v) as PageSize);
        }} className={selectClasses + ' w-full sm:w-auto'} style={selectStyle} aria-label="Products per page" data-id="element-132" data-mp="pxxfmesxtkos">
            {PAGE_SIZE_OPTIONS.map(o => <option key={String(o.value)} value={String(o.value)} data-id="element-133" data-mp="2p4vse84pxads">
                {o.label}
              </option>)}
          </select>
        </label>
      </div>
    </div>;
}
function getPageList(current: number, total: number): (number | 'gap')[] {
  if (total <= 7) {
    return Array.from({
      length: total
    }, (_, i) => i + 1);
  }
  const pages: (number | 'gap')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('gap');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('gap');
  pages.push(total);
  return pages;
}
export function ListingPagination({
  page,
  totalPages,
  onPageChange
}: {
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  const pages = getPageList(page, totalPages);
  const btnBase = 'inline-flex items-center justify-center h-9 min-w-9 px-3 rounded-md border text-sm font-medium transition-colors';
  return <nav className="flex items-center justify-center gap-1.5 flex-wrap" aria-label="Pagination" data-id="element-134" data-mp="dsf9c84sxfxk">
      <button type="button" onClick={() => onPageChange(page - 1)} disabled={page <= 1} className={`${btnBase} border-brand-border bg-white text-brand-charcoal hover:border-brand-maroon hover:text-brand-maroon disabled:opacity-40 disabled:pointer-events-none gap-1`} data-id="element-135" data-mp="3d8bzmgx3hkhg">
        <ChevronLeft size={16} data-id="element-136" data-mp="3qzh0fmc78itd" />{' '}
        <span className="hidden sm:inline" data-id="element-137" data-mp="3r0if00sfbnfu">Previous</span>
      </button>

      {pages.map((p, idx) => p === 'gap' ? <span key={`gap-${idx}`} className="px-1 text-brand-muted select-none" aria-hidden="true" data-id="element-138" data-mp="2yhutik9im6e8">
            …
          </span> : <button key={p} type="button" onClick={() => onPageChange(p)} aria-current={p === page ? 'page' : undefined} className={`${btnBase} ${p === page ? 'border-brand-maroon bg-brand-maroon text-white' : 'border-brand-border bg-white text-brand-charcoal hover:border-brand-maroon hover:text-brand-maroon'}`} data-id="element-139" data-mp="2lz916hd9dkrv">
            {p}
          </button>)}

      <button type="button" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages} className={`${btnBase} border-brand-border bg-white text-brand-charcoal hover:border-brand-maroon hover:text-brand-maroon disabled:opacity-40 disabled:pointer-events-none gap-1`} data-id="element-140" data-mp="238hmd0ai5263">
        <span className="hidden sm:inline" data-id="element-141" data-mp="1p8q7yyg2pjrk">Next</span>{' '}
        <ChevronRight size={16} data-id="element-142" data-mp="1ka59p4kdta5r" />
      </button>
    </nav>;
}